<template>
  <div class="avatar-upload flex flex-col items-center">
    <div
      class="avatar-upload-preview group relative cursor-pointer overflow-hidden transition-all hover:opacity-80 shadow-sm"
      :class="{ 'rounded-full': shape === 'circle', 'rounded-lg': shape === 'rect' }"
      :style="{ width: normalizedWidth, height: normalizedHeight }"
      @click="openModal"
    >
      <img
        v-if="displayImage"
        :src="displayImage"
        alt="Avatar Preview"
        class="h-full w-full object-cover"
      />
      <div
        v-else
        class="flex h-full w-full items-center justify-center bg-neutral-200 dark:bg-neutral-800"
      >
        <UiIcon icon="user" :size="iconSize" class="text-neutral-400" />
      </div>

      <div class="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
        <UiIcon icon="camera" size="24" class="text-white" />
      </div>
    </div>

    <!-- Actions for staged image -->
    <div v-if="stagedImage" class="mt-4 flex gap-2 w-full justify-center">
      <UiButton
        type="danger"
        size="sm"
        icon="rotate"
        class="uppercase text-xs"
        :disabled="isUploading"
        v-tooltip
        :title="$t('button.revert')"
        @click="revertUpload"
      />
      <UiButton
        type="success"
        size="sm"
        icon="check"
        :loading="isUploading"
        class="uppercase text-xs px-4"
        @click="submitUpload"
      >
        {{ $t('button.send') }}
      </UiButton>
    </div>

    <UiModal
      v-model="modalOpen"
      :title="$t('component.avatarUpload.title')"
      :width="600"
      @before-close="handleClose"
    >
      <div class="avatar-upload-modal-content flex flex-col items-center gap-6">
        <div
          v-if="!tempImage"
          class="drop-zone flex h-64 w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-neutral-300 transition-colors hover:border-primary-500 dark:border-neutral-600 cursor-pointer"
          @dragover.prevent
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
        >
          <UiIcon icon="upload" size="48" class="mb-4 text-neutral-400" />
          <p class="text-neutral-500 text-center px-4">{{ $t('component.avatarUpload.dragDrop') }}</p>
          <p class="text-xs text-neutral-400 mt-2">{{ allowed.join(', ').toUpperCase() }}</p>
          <input
            ref="fileInput"
            type="file"
            class="hidden"
            :accept="acceptString"
            @change="handleFileChange"
          />
        </div>

        <div v-show="tempImage" class="croppie-container-wrapper w-full overflow-hidden">
          <div ref="croppieEl" class="croppie-el"></div>

          <div class="flex justify-center gap-4 mt-4">
            <UiButton
              type="ghost"
              icon="rotate"
              round
              @click="cropInstance?.rotate(-90)"
            />
            <UiButton
              type="ghost"
              icon="rotate-clockwise"
              round
              @click="cropInstance?.rotate(90)"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex w-full justify-end gap-3">
          <UiButton type="danger" outline class="px-4 py-2" @click="handleCancel">
            {{ $t('button.cancel') }}
          </UiButton>
          <UiButton
            v-if="tempImage"
            type="primary"
            :loading="isProcessing"
            class="px-4 py-2"
            @click="handleConfirm"
          >
            {{ $t('button.confirm') }}
          </UiButton>
          <UiButton
            v-else
            type="primary"
            class="px-4 py-2"
            @click="triggerFileInput"
          >
            {{ $t('component.avatarUpload.selectImage') }}
          </UiButton>
        </div>
      </template>
    </UiModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch, nextTick } from 'vue'
import Croppie from 'croppie'
import 'croppie/croppie.css'

import UiModal from '@/components/ui/modal.vue'
import UiButton from '@/components/ui/button.vue'
import UiIcon from '@/components/ui/icon.vue'

interface Props {
  modelValue?: string
  shape?: 'circle' | 'rect'
  width?: string | number
  height?: string | number
  allowed?: string[]
  croppieOptions?: any
  onConfirm?: (data: string) => boolean | Promise<boolean>
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  shape: 'circle',
  width: 150,
  height: 150,
  allowed: () => ['jpg', 'jpeg', 'png', 'webp', 'svg'],
  croppieOptions: () => ({})
})

const emit = defineEmits(['update:modelValue'])

// State
const modalOpen = ref(false)
const tempImage = ref<string | null>(null)
const stagedImage = ref<string | null>(null)
const isProcessing = ref(false)
const isUploading = ref(false)
const croppieEl = ref<HTMLElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const cropInstance = ref<Croppie | null>(null)

// Computed
const displayImage = computed(() => stagedImage.value || props.modelValue)
const normalizedWidth = computed(() => typeof props.width === 'number' ? `${props.width}px` : props.width)
const normalizedHeight = computed(() => typeof props.height === 'number' ? `${props.height}px` : props.height)
const iconSize = computed(() => {
  const w = parseInt(String(props.width))
  return Math.min(w * 0.4, 64)
})
const acceptString = computed(() => props.allowed.map(ext => `.${ext}`).join(','))

// Lifecycle
onBeforeUnmount(() => {
  destroyCroppie()
})

// Functions
const openModal = () => {
  modalOpen.value = true
  tempImage.value = null
}

const triggerFileInput = () => {
  fileInput.value?.click()
}

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    loadFile(target.files[0])
  }
}

const handleDrop = (event: DragEvent) => {
  const files = event.dataTransfer?.files
  if (files && files[0]) {
    loadFile(files[0])
  }
}

const loadFile = (file: File) => {
  const reader = new FileReader()
  reader.onload = (e) => {
    tempImage.value = e.target?.result as string
    initCroppie()
  }
  reader.readAsDataURL(file)
}

const initCroppie = async () => {
  destroyCroppie()
  await nextTick()

  if (!croppieEl.value || !tempImage.value) return

  const options = {
    viewport: {
      width: Number(props.width),
      height: Number(props.height),
      type: props.shape === 'circle' ? 'circle' : 'square'
    },
    boundary: {
      width: Math.max(Number(props.width) + 100, 400),
      height: Math.max(Number(props.height) + 100, 400)
    },
    enableExif: true,
    enableOrientation: true,
    showZoomer: true,
    ...props.croppieOptions
  }

  cropInstance.value = new Croppie(croppieEl.value, options)
  cropInstance.value.bind({
    url: tempImage.value
  })
}

const destroyCroppie = () => {
  if (cropInstance.value) {
    cropInstance.value.destroy()
    cropInstance.value = null
  }
}

const handleConfirm = async () => {
  if (!cropInstance.value) return

  isProcessing.value = true
  try {
    const result = await cropInstance.value.result({
      type: 'base64',
      size: 'viewport',
      format: 'png',
      quality: 0.9,
      circle: props.shape === 'circle'
    })

    stagedImage.value = result
    modalOpen.value = false
  } catch (error) {
    console.error('Error cropping image:', error)
  } finally {
    isProcessing.value = false
  }
}

const submitUpload = async () => {
  if (!stagedImage.value) return

  isUploading.value = true
  try {
    const result = props.onConfirm ? await props.onConfirm(stagedImage.value) : true

    if (result) {
      emit('update:modelValue', stagedImage.value)
      stagedImage.value = null

      return true
    }
  } catch (error) {
    console.error('Error submitting upload:', error)
  } finally {
    isUploading.value = false
  }

  return false
}

const revertUpload = () => {
  stagedImage.value = null
}

const handleCancel = () => {
  modalOpen.value = false
}

const handleClose = () => {
  destroyCroppie()
}

watch(modalOpen, (val) => {
  if (!val) {
    destroyCroppie()
  }
})

defineExpose({
  hasStagedImage: computed(() => !!stagedImage.value),
  submitUpload
})
</script>

<style lang="scss" scoped>
.avatar-upload {
  display: inline-block;
}

.croppie-container-wrapper {
  :deep(.cr-boundary) {
    @apply rounded-lg overflow-hidden;
    background-color: transparent !important;
  }

  :deep(.cr-slider-wrap) {
    @apply mt-6;
  }

  :deep(.cr-slider) {
    @apply w-full h-1 bg-neutral-200 rounded-lg appearance-none cursor-pointer dark:bg-neutral-700;

    &::-webkit-slider-thumb {
      @apply w-4 h-4 bg-[--c-primary-600] rounded-full appearance-none;
    }
  }
}
</style>
